import { useIntl } from 'react-intl';

import type { Document as DocumentModel } from '#domain/Document.ts';

const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'];

const pdfSVG = (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 16 16'>
        <path
            fill='none'
            stroke='#ed8796'
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M2.8 14.34c1.81-1.25 3.02-3.16 3.91-5.5c.9-2.33 1.86-4.33 1.44-6.63c-.06-.36-.57-.73-.83-.7c-1.02.06-.95 1.21-.85 1.9c.24 1.71 1.56 3.7 2.84 5.56c1.27 1.87 2.32 2.16 3.78 2.26c.5.03 1.25-.14 1.37-.58c.77-2.8-9.02-.54-12.28 2.08c-.4.33-.86 1-.6 1.46c.2.36.87.4 1.23.15h0Z'
        />
    </svg>
);

const imageSVG = (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 16 16'>
        <g fill='none' strokeLinecap='round' strokeLinejoin='round'>
            <path
                stroke='#eed49f'
                d='M11.5 6A1.5 1.5 0 0 1 10 7.5A1.5 1.5 0 0 1 8.5 6A1.5 1.5 0 0 1 10 4.5A1.5 1.5 0 0 1 11.5 6'
            />
            <path stroke='#a6da95' d='M7.5 13.5L11 10c.5-.5 1.5-.5 2 0l1.5 1.5' />
            <path stroke='#a6da95' d='m1.5 9.5l2-2C4 7 5 7 5.5 7.5l4 4' />
            <path
                stroke='#7dc4e4'
                d='M3 2.5h10c.83 0 1.5.67 1.5 1.5v8c0 .83-.67 1.5-1.5 1.5H3A1.5 1.5 0 0 1 1.5 12V4c0-.83.67-1.5 1.5-1.5'
            />
        </g>
    </svg>
);

type DocumentProps = {
    doc: DocumentModel;
    onDocPress: (doc: DocumentModel) => void;
};

export const Document = ({ doc, onDocPress }: DocumentProps) => {
    const intl = useIntl();
    const { name, fileName } = doc;
    const isImage = imageExtensions.some(ext => fileName?.toLowerCase().endsWith(ext));
    const srcClasses = 'width-30 aspect-ratio-1 rounded';

    const handleShowDocumentsDialog = () => {
        onDocPress(doc);
    };

    const originLabel =
        doc.origin === 'driver'
            ? intl.formatMessage({
                  id: 'intl-msg:ordercom-cloud-frontend.order.documents.origin.driver',
                  defaultMessage: 'Driver',
              })
            : doc.origin === 'dispatcher'
              ? intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.order.documents.origin.dispatcher',
                    defaultMessage: 'Dispatcher',
                })
              : intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.order.documents.origin.unknown',
                    defaultMessage: 'Unknown origin',
                });

    return (
        <div
            className='padding-y-5 padding-left-5 padding-right-10 rounded display-flex gap-10 align-items-center cursor-pointer hover-bg-lightest shadow-subtle border border-color-lighter'
            onClick={handleShowDocumentsDialog}
        >
            <div>
                <div className={`${srcClasses} display-grid place-items-center`}>{isImage ? imageSVG : pdfSVG}</div>
            </div>

            <div className='flex-1-1-0 display-flex gap-10 align-items-center max-width-100pct'>
                <div className='width-0 flex-1-1 text-bold ellipsis-1' title={name}>
                    {name}
                </div>
                <div className='label label-muted label-filled label-condensed'>{originLabel}</div>
            </div>
        </div>
    );
};
